import { useState, useMemo, useEffect, useRef, useContext } from "react";
import ReactDOM from "react-dom";
import { getToken } from "./tokenStorage";
import QRCode from "qrcode";
import { AuthCtx } from "./AuthProvider";
import OnlineIndicator from "./OnlineIndicator";


function getAuthHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const ADMIN_USERS_URL = `${window.location.origin}/auth/admin/users`;

const ROLE_CONFIG = {
  student: { label: "Студент",        short: "S", color: "text-violet-600", bg: "bg-violet-50", border: "border-violet-200", dot: "bg-violet-400", rank: 0 },
  teacher: { label: "Преподаватель",  short: "T", color: "text-blue-600",   bg: "bg-blue-50",   border: "border-blue-200",   dot: "bg-blue-400",   rank: 1 },
  admin:   { label: "Администратор",  short: "A", color: "text-amber-600",  bg: "bg-amber-50",  border: "border-amber-200",  dot: "bg-amber-400",  rank: 2 },
};

function formatLastSeen(lastSeenIso) {
  if (!lastSeenIso) return null;
  const iso = lastSeenIso.endsWith("Z") || lastSeenIso.includes("+") ? lastSeenIso : lastSeenIso + "Z";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return "только что";
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "только что";
  if (mins < 60) return `${mins} мин назад`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} ч назад`;
  return `${Math.floor(hours / 24)} д назад`;
}

function resolveAvatarSrc(avatarUrl) {
  if (!avatarUrl) return null;
  if (/^https?:\/\//i.test(avatarUrl) || avatarUrl.startsWith("data:")) return avatarUrl;
  return `${window.location.origin}${avatarUrl}`;
}

const AVATAR_GRADIENTS = [
  "linear-gradient(135deg,#3b82f6 0%,#2563eb 60%,#7c3aed 100%)",
  "linear-gradient(135deg,#06b6d4 0%,#0ea5e9 60%,#6366f1 100%)",
  "linear-gradient(135deg,#10b981 0%,#059669 60%,#0891b2 100%)",
  "linear-gradient(135deg,#f43f5e 0%,#ec4899 60%,#a855f7 100%)",
  "linear-gradient(135deg,#f97316 0%,#ef4444 60%,#dc2626 100%)",
  "linear-gradient(135deg,#52555e 0%,#2a2c32 60%,#1a1b1f 100%)",
];

function UserRow({ user, onRoleChange, onCuratorToggle, onDeveloperToggle, onDelete, loading, currentUser, isLast, animIndex }) {
  const cfg = ROLE_CONFIG[user.role] || ROLE_CONFIG.student;
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const menuRef = useRef(null);
  const btnRef = useRef(null);
  const isCuratable = user.role === "teacher" || user.role === "admin";
  const canPromote = cfg.rank < 2;
  const canDemote = cfg.rank > 0;
  const isOnline = Boolean(user.is_online);
  const avatarSrc = resolveAvatarSrc(user.avatar_url);
  const avatarVisible = Boolean(avatarSrc) && !avatarFailed;
  const gradient = AVATAR_GRADIENTS[(user.id || 0) % AVATAR_GRADIENTS.length];
  const promoteRole = { student: "teacher", teacher: "admin" }[user.role];
  const demoteRole  = { teacher: "student", admin: "teacher" }[user.role];

  useEffect(() => {
    if (!menuPos) return;
    const handleDown = (e) => {
      if (!menuRef.current?.contains(e.target) && !btnRef.current?.contains(e.target)) setMenuPos(null);
    };
    const handleScroll = () => setMenuPos(null);
    document.addEventListener("pointerdown", handleDown);
    window.addEventListener("scroll", handleScroll, { passive: true, capture: true });
    return () => {
      document.removeEventListener("pointerdown", handleDown);
      window.removeEventListener("scroll", handleScroll, { capture: true });
    };
  }, [menuPos]);

  const onlineLabel = isOnline
    ? (user.is_active_page === false ? "отошёл" : "онлайн")
    : user.last_seen ? formatLastSeen(user.last_seen) : null;

  const subtitle = [cfg.label, user.is_curator ? "Куратор" : null, user.study_group || null].filter(Boolean).join(" · ");

  const goToProfile = () => {
    if (user.id === currentUser?.id) window.location.href = "/account";
    else window.open(`/profile/${user.id}`, "_self");
  };

  const close = () => setMenuPos(null);

  return (
    <div
      className="group relative flex items-center gap-2.5 bg-white dark:bg-transparent px-3 py-2 hover:bg-slate-50/70 dark:hover:bg-slate-700/30 active:bg-slate-100 dark:active:bg-slate-700/40 transition-colors duration-100"
      style={{ animation: "userRowIn 220ms cubic-bezier(0.2,0.9,0.2,1) both", animationDelay: `${Math.min(animIndex || 0, 14) * 28}ms`, WebkitTapHighlightColor: "transparent" }}
    >
      <button type="button" onClick={goToProfile} className="relative shrink-0 focus:outline-none">
        <div className="w-9 h-9 rounded-full overflow-hidden flex items-center justify-center" style={{ background: avatarVisible ? undefined : gradient }}>
          {avatarVisible
            ? <img src={avatarSrc} alt="" className="w-full h-full object-cover" onError={() => setAvatarFailed(true)} />
            : <span className="text-[13px] font-semibold text-white select-none leading-none">{(user.full_name || user.name || "?")[0].toUpperCase()}</span>
          }
        </div>
        {user.avatar_frame && (
          <img src={resolveAvatarSrc(user.avatar_frame)} alt="" className="absolute pointer-events-none select-none" style={{ inset: 0, width: '100%', height: '100%', zIndex: 10, transform: 'scale(1.2)', transformOrigin: 'center' }} />
        )}
        <span className="absolute bottom-0 right-0" style={{ zIndex: 11 }}>
          <OnlineIndicator online={isOnline} device={user.device || user.last_device} size={10} deviceSize={14} />
        </span>
      </button>

      <button type="button" onClick={goToProfile} className="flex-1 min-w-0 text-left focus:outline-none">
        <div className="flex items-center gap-1 min-w-0">
          <span className="text-[14px] font-medium text-slate-900 dark:text-slate-100 leading-tight truncate">{user.full_name || user.name || "—"}</span>
          {(user.is_developer && user.dev_badge_equipped !== false) && (
            <img src="/badges/developer_status.png" alt="Разработчик" title="Разработчик"
              className="shrink-0" style={{ width: 20, height: 20 }} />
          )}
          {user.equipped_badge_asset && (
            <img src={user.equipped_badge_asset} alt="" title="Значок"
              className="shrink-0" style={{ width: 20, height: 20, objectFit: 'contain' }} />
          )}
          {onlineLabel && (
            <span className={`shrink-0 text-[11px] font-medium leading-tight ${isOnline ? (user.is_active_page === false ? "text-amber-400" : "text-emerald-500") : "text-slate-400 dark:text-slate-500"}`}>
              {onlineLabel}
            </span>
          )}
        </div>
        <p className="text-[11px] text-slate-400 dark:text-slate-500 leading-tight mt-0.5 truncate">{subtitle}</p>
      </button>

      {/* Skrep balance */}
      <span className="flex items-center gap-0.5 text-[11px] font-medium text-slate-400 dark:text-slate-500 shrink-0">
        <img src="/paperclip.svg" alt="" style={{ width: 12, height: 12, opacity: 0.5 }} />
        {(user.skrep_balance ?? 0)}
      </span>

      {/* kebab menu */}
      <div className="relative shrink-0">
        <button
          ref={btnRef}
          type="button"
          disabled={loading}
          onClick={(e) => {
            e.stopPropagation();
            if (menuPos) { setMenuPos(null); return; }
            const rect = btnRef.current.getBoundingClientRect();
            setMenuPos({
              top: rect.top + rect.height / 2 + window.scrollY,
              right: window.innerWidth - rect.left + 3,
            });
          }}
          className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${menuPos ? "bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200" : "text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700/40"}`}
        >
          <svg width="14" height="14" viewBox="0 0 4 16" fill="currentColor">
            <circle cx="2" cy="2" r="1.5"/><circle cx="2" cy="8" r="1.5"/><circle cx="2" cy="14" r="1.5"/>
          </svg>
        </button>

        {menuPos && ReactDOM.createPortal(
          <div ref={menuRef} style={{ position: "absolute", top: menuPos.top, right: menuPos.right, transform: "translateY(-50%)", zIndex: 9999, animation: "kebabMenuIn 160ms cubic-bezier(0.2,0.9,0.4,1) both" }}
            className="w-max bg-white dark:bg-[#2d3040] rounded-xl shadow-[0_8px_24px_-4px_rgba(15,23,42,0.16),0_0_0_1px_rgba(15,23,42,0.06)] dark:shadow-[0_8px_24px_-4px_rgba(0,0,0,0.5),0_0_0_1px_rgba(255,255,255,0.06)] py-1 overflow-hidden"
          >
            {canPromote && (
              <button type="button" onClick={() => { onRoleChange(user.id, promoteRole); close(); }}
                className="flex items-center gap-2 w-full px-2.5 py-1.5 text-[13px] text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors text-left whitespace-nowrap"
              >
                <svg className="w-3.5 h-3.5 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7"/></svg>
                {ROLE_CONFIG[promoteRole]?.label}
              </button>
            )}
            {canDemote && (
              <button type="button" onClick={() => { onRoleChange(user.id, demoteRole); close(); }}
                className="flex items-center gap-2 w-full px-2.5 py-1.5 text-[13px] text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors text-left whitespace-nowrap"
              >
                <svg className="w-3.5 h-3.5 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7"/></svg>
                {ROLE_CONFIG[demoteRole]?.label}
              </button>
            )}
            {isCuratable && (
              <button type="button" onClick={() => { onCuratorToggle(user.id, !user.is_curator); close(); }}
                className="flex items-center gap-2 w-full px-2.5 py-1.5 text-[13px] text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors text-left whitespace-nowrap"
              >
                <svg className="w-3.5 h-3.5 text-emerald-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                {user.is_curator ? "Снять куратора" : "Сделать куратором"}
              </button>
            )}
            <button type="button" onClick={() => { onDeveloperToggle(user.id, !user.is_developer); close(); }}
              className="flex items-center gap-2 w-full px-2.5 py-1.5 text-[13px] text-slate-700 hover:bg-slate-50 transition-colors text-left whitespace-nowrap"
            >
              <svg className="w-3.5 h-3.5 text-violet-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/></svg>
              {user.is_developer ? "Снять разработчика" : "Сделать разработчиком"}
            </button>
            <div className="h-px bg-slate-100 dark:bg-slate-700/50 mx-1.5 my-0.5" />
            <button type="button" onClick={() => { onDelete(user.id, user.full_name || user.name); close(); }}
              className="flex items-center gap-2 w-full px-2.5 py-1.5 text-[13px] text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-left whitespace-nowrap"
            >
              <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
              Удалить
            </button>
          </div>,
          document.body
        )}
      </div>

      {!isLast && <div className="pointer-events-none absolute bottom-0 left-[58px] right-0 h-px bg-slate-100 dark:bg-slate-700/50" />}
    </div>
  );
}

// ── Schedule Parse Tab ──────────────────────────────────────────────────────

// Parse log line: "[HH:MM:SS] TAG text" → styled spans
function ParseLogLine({ text, level }) {
  // Server lines have the "[HH:MM:SS] TAG" prefix
  const m = text.match(/^(\[\d{2}:\d{2}:\d{2}\])\s+(INF|WRN|ERR|DBG)\s+(.+)$/s);
  if (m) {
    const tagColor = { INF: "text-slate-500", WRN: "text-amber-500", ERR: "text-red-500", DBG: "text-slate-600" }[m[2]] || "text-slate-500";
    return (
      <div className="flex gap-1.5 min-w-0">
        <span className="text-slate-600 shrink-0 select-none">{m[1]}</span>
        <span className={`${tagColor} shrink-0 select-none`}>{m[2]}</span>
        <span className="text-slate-300 break-all">{m[3]}</span>
      </div>
    );
  }
  // Client-side lines
  const cls = {
    error: "text-red-400", success: "text-emerald-400",
    warn: "text-amber-400", info: "text-slate-400",
  }[level] || "text-slate-400";
  return <div className={`${cls} break-all`}>{text}</div>;
}

const NOTIFY_OPTIONS = [
  { v: "all",           label: "Все пуши",             hint: "Новое + изменения" },
  { v: "new_week_only", label: "Только новая неделя",  hint: "Без изменений" },
  { v: "changes_only", label: "Только изменения",      hint: "Без новой недели" },
  { v: "none",          label: "Без уведомлений",      hint: "Тихий парсинг" },
  { v: "dry_run",       label: "Dry-run",              hint: "Симуляция" },
];

function haptic(pattern) {
  try {
    if (typeof navigator !== "undefined" && navigator.vibrate) navigator.vibrate(pattern);
  } catch { /* no-op */ }
}

function NotifyDropdown({ value, onChange, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = NOTIFY_OPTIONS.find(o => o.v === value) || NOTIFY_OPTIONS[0];

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("pointerdown", h);
    return () => document.removeEventListener("pointerdown", h);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => { haptic(8); setOpen(o => !o); }}
        className={`h-8 pl-3 pr-2 rounded-[10px] flex items-center gap-1.5 text-[12px] font-medium bg-[#1e2330] text-slate-200 border border-[#2d3448] transition-all duration-150 active:scale-[0.97] hover:border-[#3a4258] disabled:opacity-40 ${open ? "border-blue-500/60" : ""}`}
      >
        <span className="whitespace-nowrap">{current.label}</span>
        <svg
          className="w-3 h-3 text-slate-500"
          viewBox="0 0 12 12" fill="none"
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0)",
            transition: "transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1)",
          }}
        >
          <path d="M2.5 4.5 L6 8 L9.5 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute right-0 bottom-full mb-1.5 min-w-[180px] rounded-[12px] bg-[#1a1f2e] border border-[#2d3448] shadow-[0_12px_32px_-12px_rgba(0,0,0,0.6)] overflow-hidden z-20"
          style={{
            transformOrigin: "bottom right",
            animation: "notifyDropIn 220ms cubic-bezier(0.2, 0.9, 0.2, 1)",
          }}
        >
          {NOTIFY_OPTIONS.map((opt) => (
            <button
              key={opt.v}
              type="button"
              onClick={() => { haptic(6); onChange(opt.v); setOpen(false); }}
              className={`w-full px-3 py-2 flex items-center justify-between gap-3 text-left transition-colors ${
                opt.v === value
                  ? "bg-blue-500/[0.14] text-blue-300"
                  : "text-slate-300 hover:bg-white/[0.03]"
              }`}
            >
              <div className="min-w-0">
                <div className="text-[12px] font-medium leading-tight whitespace-nowrap">{opt.label}</div>
                <div className="text-[10px] text-slate-500 leading-tight mt-0.5">{opt.hint}</div>
              </div>
              {opt.v === value && (
                <svg className="w-3.5 h-3.5 text-blue-400 shrink-0" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8.5l3.5 3.5L13 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ScheduleParseTab() {
  const [parseMode, setParseMode] = useState("current");
  const [notifyMode, setNotifyMode] = useState("all");
  const [parsing, setParsing] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logSource, setLogSource] = useState("parse"); // "parse" | "reg"
  const [regLoading, setRegLoading] = useState(false);
  const consoleRef = useRef(null);
  const pollTimerRef = useRef(null);
  const abortRef = useRef(null);

  const fetchRegLogs = async () => {
    setRegLoading(true);
    setLogs([]);
    try {
      const res = await fetch(`${window.location.origin}/auth/admin/reg-logs`, { headers: getAuthHeaders() });
      if (!res.ok) { addLog(`✗ ошибка ${res.status}`, "error"); return; }
      const data = await res.json();
      const entries = data.logs || [];
      if (entries.length === 0) { addLog("— логов нет (ещё не было регистраций)", "info"); }
      else setLogs(entries.map(l => ({ text: l, level: "server" })));
    } catch (err) {
      addLog(`✗ сеть: ${err.message}`, "error");
    } finally {
      setRegLoading(false);
    }
  };

  const fetchStatus = async () => {
    try {
      const res = await fetch(
        `${window.location.origin}/schedule/reparse-status`,
        { headers: getAuthHeaders() }
      );
      if (!res.ok) return null;
      return await res.json();
    } catch { return null; }
  };

  const addLog = (text, level = "info") =>
    setLogs(prev => [...prev, { text, level }]);

  useEffect(() => {
    fetchStatus().then(s => {
      if (!s) return;
      if (Array.isArray(s.logs) && s.logs.length > 0)
        setLogs(s.logs.map(l => ({ text: l, level: "server" })));
    });
  }, []);

  useEffect(() => {
    if (consoleRef.current)
      consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
  }, [logs]);

  const startPolling = () => {
    const poll = async () => {
      const s = await fetchStatus();
      if (!s) { pollTimerRef.current = setTimeout(poll, 2000); return; }
      if (Array.isArray(s.logs)) {
        setLogs(prev => {
          const seen = prev.filter(l => l.level === "server").length;
          const fresh = s.logs.slice(seen);
          return fresh.length
            ? [...prev, ...fresh.map(l => ({ text: l, level: "server" }))]
            : prev;
        });
      }
      if (s.is_running) pollTimerRef.current = setTimeout(poll, 2000);
    };
    pollTimerRef.current = setTimeout(poll, 1500);
  };

  const stopPolling = () => { clearTimeout(pollTimerRef.current); pollTimerRef.current = null; };

  const handleParse = async () => {
    if (parsing) return;
    stopPolling();
    setParsing(true);
    setLogs([]);
    const notifyLabel = NOTIFY_OPTIONS.find(o => o.v === notifyMode)?.label || notifyMode;
    addLog(`› парсинг запущен (режим: ${parseMode}, пуши: ${notifyLabel})`, "info");

    const controller = new AbortController();
    abortRef.current = controller;
    startPolling();

    try {
      const res = await fetch(
        `${window.location.origin}/schedule/reparse?mode=${parseMode}&force=true&notify=${notifyMode}`,
        { method: "POST", headers: getAuthHeaders(), signal: controller.signal }
      );
      stopPolling();
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        addLog(`✗ ошибка ${res.status}: ${data.detail || res.statusText}`, "error");
      } else if (data.status === "fresh") {
        const m = Math.round((data.next_allowed_in_seconds || 0) / 60);
        addLog(`⚠ кэш актуален, следующий через ${m} мин`, "warn");
      } else {
        addLog(`✓ готово: ${data.count ?? "?"} записей · ${data.range_id ?? "—"}`, "success");
        if ((data.range_ids?.length ?? 0) > 1)
          addLog(`  недели: ${data.range_ids.join(", ")}`, "info");
      }
      const s = await fetchStatus();
      if (s) {
          if (Array.isArray(s.logs) && s.logs.length > 0) {
          setLogs(prev => {
            const seen = prev.filter(l => l.level === "server").length;
            const fresh = s.logs.slice(seen);
            return fresh.length
              ? [...prev, ...fresh.map(l => ({ text: l, level: "server" }))]
              : prev;
          });
        }
      }
    } catch (err) {
      stopPolling();
      if (err.name !== "AbortError") addLog(`✗ сеть: ${err.message}`, "error");
    } finally {
      setParsing(false);
      abortRef.current = null;
    }
  };

  const hasLogs = logs.length > 0;

  const handleParseClick = () => { haptic([10, 20, 10]); handleParse(); };
  const handleRegClick = () => { haptic(10); fetchRegLogs(); };

  return (
    <div
      className="rounded-[16px] overflow-hidden bg-[#161b27] border border-white/[0.04] shadow-[0_8px_28px_-18px_rgba(0,0,0,0.65)] flex flex-col"
      style={{ animation: "parsePanelIn 320ms cubic-bezier(0.2, 0.9, 0.2, 1)" }}
    >
      <style>{`
        @keyframes parsePanelIn {
          from { opacity: 0; transform: translateY(8px) scale(0.985); filter: blur(4px); }
          to   { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        @keyframes notifyDropIn {
          from { opacity: 0; transform: translateY(6px) scale(0.96); filter: blur(3px); }
          to   { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        @keyframes pulseDot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.35; transform: scale(0.75); }
        }
      `}</style>

      {/* ─── Tabs ─── */}
      <div className="flex items-center gap-1 px-2 pt-2 pb-0 bg-[#161b27]">
        {[{ v: "parse", label: "Парсинг" }, { v: "reg", label: "Регистрации" }].map(({ v, label }) => (
          <button
            key={v}
            type="button"
            onClick={() => { haptic(6); setLogSource(v); setLogs([]); }}
            className={`relative px-3 py-1.5 text-[12px] font-medium transition-colors ${
              logSource === v ? "text-slate-100" : "text-slate-500 hover:text-slate-300"
            }`}
          >
            {label}
            {logSource === v && (
              <span
                className="absolute left-2 right-2 -bottom-px h-[2px] bg-blue-500 rounded-full"
                style={{ animation: "parsePanelIn 220ms cubic-bezier(0.34, 1.56, 0.64, 1)" }}
              />
            )}
          </button>
        ))}
        <div className="flex-1" />
        {parsing && (
          <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-blue-500/[0.14] text-blue-400 text-[10px] font-semibold tracking-wider uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400" style={{ animation: "pulseDot 1.1s ease-in-out infinite" }} />
            идёт
          </div>
        )}
        {hasLogs && !parsing && (
          <button
            type="button"
            onClick={() => { haptic(6); setLogs([]); }}
            className="text-slate-500 hover:text-slate-300 transition-colors p-1 rounded-md"
            title="Очистить"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14H6L5 6"/>
              <path d="M10 11v6M14 11v6"/>
              <path d="M9 6V4h6v2"/>
            </svg>
          </button>
        )}
      </div>
      <div className="h-px bg-white/[0.04]" />

      {/* ─── Console body ─── */}
      <div
        ref={consoleRef}
        className="px-3 py-2.5 h-56 overflow-y-auto font-mono text-[11.5px] leading-[1.65] space-y-px"
        style={{ scrollbarWidth: "thin", scrollbarColor: "#2d3448 transparent" }}
      >
        {!hasLogs ? (
          <span className="text-slate-700">_ ожидание запуска…</span>
        ) : (
          logs.map((log, i) => <ParseLogLine key={i} text={log.text} level={log.level} />)
        )}
        {(parsing || regLoading) && (
          <div className="inline-block w-1.5 h-3.5 bg-slate-400 rounded-sm animate-pulse align-middle ml-0.5" />
        )}
      </div>

      {/* ─── Toolbar ─── */}
      <div className="h-px bg-white/[0.04]" />
      <div className="flex items-center gap-2 px-2.5 py-2 bg-[#1a1f2e]">
        {logSource === "reg" ? (
          <>
            <div className="flex-1 text-[11px] text-slate-500">
              Логи регистраций пользователей
            </div>
            <button
              type="button"
              onClick={handleRegClick}
              disabled={regLoading}
              className="h-8 px-3.5 rounded-[10px] flex items-center gap-1.5 text-[12px] font-semibold bg-blue-500 text-white hover:bg-blue-400 transition-all duration-150 active:scale-[0.97] disabled:opacity-50"
            >
              {regLoading ? (
                <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"/>
                  <path className="opacity-85" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <polyline points="1 4 1 10 7 10"/>
                  <path d="M3.51 15a9 9 0 0 0 14.85 3.36L23 14"/>
                </svg>
              )}
              {regLoading ? "Загрузка" : "Обновить"}
            </button>
          </>
        ) : (
          <>
            {/* Mode segmented control */}
            <div className="relative inline-flex p-0.5 rounded-[10px] bg-[#1e2330] border border-[#2d3448]">
              {[
                { v: "current", label: "Текущее" },
                { v: "latest",  label: "Последнее" },
              ].map(({ v, label }) => {
                const active = parseMode === v;
                return (
                  <button
                    key={v}
                    type="button"
                    disabled={parsing}
                    onClick={() => { haptic(6); setParseMode(v); }}
                    className={`relative z-10 px-3 h-7 text-[12px] font-medium rounded-[8px] transition-colors duration-200 ${
                      active ? "text-white" : "text-slate-400 hover:text-slate-200"
                    } disabled:opacity-50`}
                  >
                    {active && (
                      <span
                        className="absolute inset-0 bg-blue-500 rounded-[8px] -z-10"
                        style={{ animation: "parsePanelIn 220ms cubic-bezier(0.34, 1.56, 0.64, 1)" }}
                      />
                    )}
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Notify dropdown */}
            <NotifyDropdown value={notifyMode} onChange={setNotifyMode} disabled={parsing} />

            <div className="flex-1" />

            {/* Primary action */}
            <button
              type="button"
              onClick={handleParseClick}
              disabled={parsing}
              className={`h-8 px-4 rounded-[10px] flex items-center gap-1.5 text-[12px] font-semibold transition-all duration-150 active:scale-[0.97] disabled:opacity-70 disabled:active:scale-100 ${
                parsing
                  ? "bg-[#1e2330] text-slate-400 border border-[#2d3448]"
                  : "bg-blue-500 text-white hover:bg-blue-400"
              }`}
            >
              {parsing ? (
                <>
                  <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3"/>
                    <path className="opacity-85" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                  </svg>
                  Парсинг
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7L8 5z" />
                  </svg>
                  Запустить
                </>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main AdminAccessPanel ────────────────────────────────────────────────────

export default function AdminAccessPanel() {
  const [activeTab, setActiveTab] = useState("vydacha");
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, userId: null, userName: "" });
  const { user: currentAdmin } = useContext(AuthCtx);

  // Debug clip state
  const [debugClipActive, setDebugClipActive] = useState(() => !!window.__debugClipWindow);
  const [debugGolden, setDebugGolden] = useState(() => !!window.__forceGolden);
  const [debugClipId, setDebugClipId] = useState(() => window.__debugClipWindow || "");
  const [debugCandyActive, setDebugCandyActive] = useState(() => !!window.__debugCandyWindow);
  const [debugCandyId, setDebugCandyId] = useState(() => window.__debugCandyWindow || "");
  const [forceSpawnLoading, setForceSpawnLoading] = useState(false);
  const [forceSpawnResult, setForceSpawnResult] = useState(null);

  const forceSpawnServer = async () => {
    setForceSpawnLoading(true);
    setForceSpawnResult(null);
    try {
      const res = await fetch(`${window.location.origin}/skrep/admin/force-spawn`, {
        method: "POST", headers: getAuthHeaders(),
      });
      const data = await res.json();
      setForceSpawnResult(data);
      // Tell DailyPaperclip to clear its claimed-zones state immediately
      Object.keys(localStorage).filter(k => k.startsWith("skrep_")).forEach(k => localStorage.removeItem(k));
      window.dispatchEvent(new CustomEvent("debugClipReset", { detail: { action: "clearClaims" } }));
    } catch { setForceSpawnResult({ error: true }); }
    finally { setForceSpawnLoading(false); }
  };

  const spawnDebugClips = () => {
    // Simulate daily clip spawn: use real window ID + real route algorithm,
    // just reset claimed state so clips appear as if it's a fresh window.
    Object.keys(localStorage)
      .filter(k => k.startsWith("skrep_"))
      .forEach(k => localStorage.removeItem(k));
    if (debugGolden) window.__forceGolden = true; else delete window.__forceGolden;
    window.__simulateDailySpawn = true;
    const realId = `dbg_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    window.__debugClipWindow = realId; // enables all-zones debug rendering
    setDebugClipId(realId);
    setDebugClipActive(true);
    window.dispatchEvent(new CustomEvent("debugClipReset", { detail: { action: "clearClaims" } }));
  };

  const resetDebugClips = () => {
    delete window.__debugClipWindow;
    delete window.__forceClip;
    delete window.__forceGolden;
    delete window.__debugClipCount;
    delete window.__simulateDailySpawn;
    setDebugClipActive(false);
    setDebugGolden(false);
    setDebugClipId("");
    window.dispatchEvent(new CustomEvent("debugClipReset", { detail: { action: "clear" } }));
  };

  const spawnDebugCandy = () => {
    const dbgId = `dbg_candy_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    window.__debugCandyWindow = dbgId;
    setDebugCandyId(dbgId);
    setDebugCandyActive(true);
    window.dispatchEvent(new CustomEvent("debugCandyReset"));
  };

  const resetDebugCandy = () => {
    delete window.__debugCandyWindow;
    setDebugCandyActive(false);
    setDebugCandyId("");
    window.dispatchEvent(new CustomEvent("debugCandyReset"));
  };

  const rerollDebugCandy = () => {
    const dbgId = `dbg_candy_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    window.__debugCandyWindow = dbgId;
    setDebugCandyId(dbgId);
    window.dispatchEvent(new CustomEvent("debugCandyReset"));
  };

  const toggleDebugGolden = () => {
    const next = !debugGolden;
    setDebugGolden(next);
    if (next) window.__forceGolden = true;
    else delete window.__forceGolden;
  };

  // QR Rewards
  const [qrRewardType, setQrRewardType] = useState("regular");
  const [qrAmount, setQrAmount] = useState(1);
  const [qrTtl, setQrTtl] = useState(null);
  const [qrMaxClaims, setQrMaxClaims] = useState(null);
  const [qrLabel, setQrLabel] = useState("");
  const [qrCreateLoading, setQrCreateLoading] = useState(false);
  const [qrResult, setQrResult] = useState(null);
  const [qrHistory, setQrHistory] = useState([]);
  const [qrHistoryLoading, setQrHistoryLoading] = useState(false);

  const qrCanvasRef = useRef(null);

  const qrClaimUrl = qrResult
    ? (qrResult.claim_url || `${window.location.origin}/qr/${qrResult.code}`)
    : "";

  const fetchQrHistory = async () => {
    setQrHistoryLoading(true);
    try {
      const res = await fetch(`${window.location.origin}/skrep/admin/qr-rewards`, { headers: getAuthHeaders() });
      const data = await res.json();
      setQrHistory(Array.isArray(data) ? data : []);
    } catch { /* silent */ } finally { setQrHistoryLoading(false); }
  };

  const createQrReward = async () => {
    setQrCreateLoading(true);
    try {
      const res = await fetch(`${window.location.origin}/skrep/admin/qr-reward`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({
          reward_type: qrRewardType,
          reward_amount: qrAmount,
          ttl_hours: qrTtl || undefined,
          max_claims: qrMaxClaims || undefined,
          label: qrLabel.trim() || undefined,
        }),
      });
      const data = await res.json();
      setQrResult(data);
      fetchQrHistory();
    } catch { /* silent */ } finally { setQrCreateLoading(false); }
  };

  const deleteQrReward = async (id) => {
    await fetch(`${window.location.origin}/skrep/admin/qr-reward/${id}`, {
      method: "DELETE", headers: getAuthHeaders(),
    });
    setQrHistory((prev) => prev.filter((r) => r.id !== id));
    if (qrResult?.id === id) setQrResult(null);
  };

  useEffect(() => {
    if (activeTab === "vydacha") fetchQrHistory();
  }, [activeTab]);

  useEffect(() => {
    if (!qrResult || !qrCanvasRef.current) return;
    QRCode.toCanvas(qrCanvasRef.current, qrClaimUrl, {
      width: 480, margin: 2,
      color: { dark: "#0f172a", light: "#ffffff" },
      errorCorrectionLevel: "H",
    }, (err) => {
      if (err) { console.error(err); return; }
      const canvas = qrCanvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      const logoSize = Math.round(canvas.width * 0.2);
      const x = (canvas.width - logoSize) / 2;
      const y = (canvas.height - logoSize) / 2;
      const img = new Image();
      img.src = "/Logo_AudAssist.png";
      img.onload = () => {
        ctx.drawImage(img, x, y, logoSize, logoSize);
      };
    });
  }, [qrResult, qrClaimUrl]);

  const downloadQr = () => {
    const canvas = qrCanvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `qr-${qrResult?.code || "reward"}.png`;
      a.click();
    });
  };


  // Invite
  const [issuedToEmail] = useState("");
  const [ttlHours, setTtlHours] = useState(72);
  const [maxUses, setMaxUses] = useState(1);
  const [customTtl, setCustomTtl] = useState(false);
  const [customMax, setCustomMax] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [inviteCreateLoading, setInviteCreateLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);

  // Vychitka
  const [vychitkaFiles, setVychitkaFiles] = useState([]);
  const [vychitkaLoading, setVychitkaLoading] = useState(false);
  const [vychitkaUploading, setVychitkaUploading] = useState(false);
  const [vychitkaError, setVychitkaError] = useState("");
  const [vychitkaSuccess, setVychitkaSuccess] = useState("");
  const vychitkaInputRef = useRef(null);
  const vychitkaSuccessTimer = useRef(null);

  const fetchVychitka = async () => {
    setVychitkaLoading(true);
    try {
      const res = await fetch(`${window.location.origin}/admin/vychitka/list`, { headers: getAuthHeaders() });
      const data = await res.json();
      setVychitkaFiles(Array.isArray(data) ? data : []);
    } catch { setVychitkaError("Ошибка загрузки списка"); } finally { setVychitkaLoading(false); }
  };

  const handleVychitkaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".xlsx")) { setVychitkaError("Только .xlsx файлы"); return; }
    setVychitkaError("");
    setVychitkaUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${window.location.origin}/admin/vychitka/upload`, { method: "POST", headers: getAuthHeaders(), body: fd });
      if (!res.ok) { const d = await res.json().catch(() => null); setVychitkaError(d?.detail || "Ошибка загрузки"); return; }
      await fetchVychitka();
      window.dispatchEvent(new CustomEvent("vychitka:uploaded"));
      localStorage.setItem("audassist.vychitka.v", Date.now());
      clearTimeout(vychitkaSuccessTimer.current);
      setVychitkaSuccess(`Файл «${file.name}» загружен. Диаграммы обновятся автоматически.`);
      vychitkaSuccessTimer.current = setTimeout(() => setVychitkaSuccess(""), 5000);
    } catch { setVychitkaError("Ошибка сети"); } finally { setVychitkaUploading(false); if (vychitkaInputRef.current) vychitkaInputRef.current.value = ""; }
  };

  const handleVychitkaDelete = async (filename) => {
    if (!window.confirm(`Удалить «${filename}»?`)) return;
    try {
      await fetch(`${window.location.origin}/admin/vychitka/delete/${encodeURIComponent(filename)}`, { method: "DELETE", headers: getAuthHeaders() });
      setVychitkaFiles((prev) => prev.filter((f) => f.filename !== filename));
      window.dispatchEvent(new CustomEvent("vychitka:uploaded"));
      localStorage.setItem("audassist.vychitka.v", Date.now());
    } catch { setVychitkaError("Ошибка удаления"); }
  };

  useEffect(() => { if (activeTab === "data") fetchVychitka(); }, [activeTab]);

  // Users
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [roleChangeLoading, setRoleChangeLoading] = useState(false);
  const [listKey, setListKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [onlineCount, setOnlineCount] = useState(-1);
  const wsListenerRef = useRef(null);
  const usersRef = useRef([]);
  useEffect(() => { usersRef.current = users; }, [users]);

  const inviteLink = useMemo(() => {
    if (!inviteCode) return "";
    const url = new URL("/login", process.env.REACT_APP_URL || window.location.origin);
    url.searchParams.set("m", "tr");
    url.searchParams.set("i", inviteCode);
    return url.toString();
  }, [inviteCode]);

  const fetchOnlineCount = async () => {
    try {
      const res = await fetch(`${window.location.origin}/auth/admin/online-count`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.count !== undefined) { setOnlineCount(data.count); }
    } catch {}
  };

  useEffect(() => {
    if (!currentAdmin || currentAdmin.role !== "admin") return;
    const interval = setInterval(fetchOnlineCount, 2000);
    return () => clearInterval(interval);
  }, [currentAdmin]);

  useEffect(() => {
    if (!currentAdmin || currentAdmin.role !== "admin") return;
    const interval = setInterval(() => fetchUsers(false), 5000);
    return () => clearInterval(interval);
  }, [currentAdmin]);

  useEffect(() => {
    if (inviteLink && canvasRef.current) {
      QRCode.toCanvas(canvasRef.current, inviteLink, { width: 160, margin: 1, color: { dark: "#0f172a", light: "#ffffff" }, errorCorrectionLevel: "H" },
        (err) => { if (err) console.error(err); });
    }
  }, [inviteLink]);

  const fetchUsers = async (showLoading = false) => {
    if (showLoading) setUsersLoading(true);
    try {
      const res = await fetch(ADMIN_USERS_URL, { headers: getAuthHeaders() });
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.users || []);
      setUsers(list);
      if (data.online_count !== undefined) setOnlineCount(data.online_count);
    } catch (err) {
      console.error(err);
    } finally {
      if (showLoading) setUsersLoading(false);
    }
  };

  useEffect(() => { fetchUsers(true); }, []);

  useEffect(() => {
    if (activeTab === "users") fetchUsers(true);
  }, [activeTab]);

  // Periodic refresh to catch offline users whose Redis TTL expired without a WS close event
  useEffect(() => {
    const id = setInterval(() => fetchUsers(false), 12_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!currentAdmin?.id) return;
    function attachListener(ws) {
      if (wsListenerRef.current) {
        if (wsListenerRef.current.socket === ws) return;
        wsListenerRef.current.socket.removeEventListener("message", wsListenerRef.current.handler);
      }
      const handler = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === "online_count") setOnlineCount(msg.count);
          if (msg.type === "online_update") {
            setUsers((prev) => prev.map((u) =>
              u.id === msg.user_id ? { ...u, is_online: msg.is_online, last_seen: msg.last_seen ?? u.last_seen, is_active_page: msg.is_active_page ?? u.is_active_page, device: msg.is_online ? (msg.device ?? u.device) : null, last_device: msg.device ?? u.last_device } : u
            ));
          }
        } catch {}
      };
      ws.addEventListener("message", handler);
      wsListenerRef.current = { socket: ws, handler };
    }
    if (window.__presenceWS && window.__presenceWS.readyState === WebSocket.OPEN) attachListener(window.__presenceWS);
    const onWsReady = (e) => attachListener(e.detail);
    window.addEventListener("presenceWsReady", onWsReady);
    const onVisible = () => { if (window.__presenceWS && window.__presenceWS.readyState === WebSocket.OPEN) attachListener(window.__presenceWS); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("presenceWsReady", onWsReady);
      document.removeEventListener("visibilitychange", onVisible);
      if (wsListenerRef.current) { wsListenerRef.current.socket.removeEventListener("message", wsListenerRef.current.handler); wsListenerRef.current = null; }
    };
  }, [currentAdmin?.id]);

  const handleCuratorToggle = async (userId, isCurator) => {
    setRoleChangeLoading(true);
    try {
      await fetch(`${ADMIN_USERS_URL}/${userId}/curator`, { method: "PATCH", headers: { "Content-Type": "application/json", ...getAuthHeaders() }, body: JSON.stringify({ is_curator: isCurator }) });
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, is_curator: isCurator } : u)));
    } catch (err) { console.error(err); } finally { setRoleChangeLoading(false); }
  };

  const handleDeveloperToggle = async (userId, isDeveloper) => {
    setRoleChangeLoading(true);
    try {
      await fetch(`${ADMIN_USERS_URL}/${userId}/developer`, { method: "PATCH", headers: { "Content-Type": "application/json", ...getAuthHeaders() }, body: JSON.stringify({ is_developer: isDeveloper }) });
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, is_developer: isDeveloper } : u)));
    } catch (err) { console.error(err); } finally { setRoleChangeLoading(false); }
  };

  const handleRoleChange = async (userId, newRole) => {
    setRoleChangeLoading(true);
    try {
      await fetch(`${ADMIN_USERS_URL}/${userId}/role`, { method: "PATCH", headers: { "Content-Type": "application/json", ...getAuthHeaders() }, body: JSON.stringify({ role: newRole }) });
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role: newRole } : u)));
    } catch (err) { console.error(err); } finally { setRoleChangeLoading(false); }
  };

  const openDeleteModal = (userId, userName) => setDeleteConfirm({ open: true, userId, userName });

  const confirmDelete = async () => {
    const { userId } = deleteConfirm;
    setDeleteConfirm({ open: false, userId: null, userName: "" });
    setRoleChangeLoading(true);
    try {
      const res = await fetch(`${ADMIN_USERS_URL}/${userId}`, { method: "DELETE", headers: getAuthHeaders() });
      if (res.ok) { setUsers((prev) => prev.filter((u) => u.id !== userId)); return; }
      const data = await res.json().catch(() => null);
      alert(data?.detail || "Ошибка при удалении");
    } catch { alert("Ошибка сети"); } finally { setRoleChangeLoading(false); }
  };

  const createInvite = async (e) => {
    if (e) e.preventDefault();
    setInviteCreateLoading(true);
    try {
      const res = await fetch(`${window.location.origin}/auth/admin/invite-codes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ ttl_hours: Number(ttlHours), max_uses: Number(maxUses), issued_to_email: issuedToEmail.trim() || undefined }),
      });
      const data = await res.json();
      setInviteCode(data.code);
    } catch (err) { console.error(err); } finally { setInviteCreateLoading(false); }
  };

  const doRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([fetchUsers(false), fetchOnlineCount()]);
    setListKey((k) => k + 1);
    setIsRefreshing(false);
  };

  const filteredUsers = useMemo(() => users.filter((u) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.full_name?.toLowerCase().includes(q);
    if (roleFilter === "online") return matchesSearch && Boolean(u.is_online);
    return matchesSearch && (roleFilter === "all" || u.role === roleFilter);
  }), [users, searchQuery, roleFilter]);

  const roleCounts = useMemo(() => {
    const c = { student: 0, teacher: 0, admin: 0 };
    users.forEach((u) => { if (c[u.role] !== undefined) c[u.role]++; });
    return c;
  }, [users]);

  return (
    <div className="w-full px-4 py-3">
      <style>{`
        @keyframes userRowIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @keyframes onlineNumPop{0%{opacity:0;transform:scale(1.4)}100%{opacity:1;transform:scale(1)}}
        @keyframes kebabMenuIn{from{opacity:0;transform:translateY(-50%) scale(0.92)}to{opacity:1;transform:translateY(-50%) scale(1)}}
        @keyframes spinOnce{from{transform:rotate(0deg)}to{transform:rotate(-360deg)}}
        @keyframes tabContentIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
      `}</style>

      {/* Admin header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-slate-900 dark:bg-slate-700 flex items-center justify-center shrink-0">
            <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
            </svg>
          </div>
          <span className="text-[15px] font-semibold text-slate-900 dark:text-slate-100 leading-none">Панель</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200/70 dark:border-emerald-700/30 text-[11px] font-medium text-emerald-700 dark:text-emerald-400">
            <span className={`w-1.5 h-1.5 rounded-full ${onlineCount > 0 ? "bg-emerald-400" : "bg-slate-300 dark:bg-slate-600"}`} />
            <span key={onlineCount} className="tabular-nums" style={{animation:"onlineNumPop 280ms cubic-bezier(0.2,0.9,0.2,1) both"}}>{onlineCount === -1 ? "—" : onlineCount} онлайн</span>
          </span>
          <a href="/users" className="flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 dark:bg-amber-900/20 border border-amber-200/60 dark:border-amber-700/30 text-[11px] font-medium text-amber-700 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors shrink-0">
            <img src="/badges/golden_crown.svg" alt="" style={{width:12,height:12,objectFit:"contain"}} />
            Топ
          </a>
        </div>
      </div>

      {/* Tab nav — 4 tabs */}
      <div className="flex items-center gap-0.5 p-1 bg-slate-100/80 dark:bg-slate-800/60 rounded-[14px] overflow-x-auto mb-4" style={{WebkitOverflowScrolling:"touch",scrollbarWidth:"none"}}>
        {[
          { id: "users",   label: "Участники", icon: <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg> },
          { id: "vydacha", label: "Выдача",    icon: <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"/></svg> },
          { id: "data",    label: "Данные",    icon: <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><ellipse cx="12" cy="5" rx="9" ry="3"/><path strokeLinecap="round" strokeLinejoin="round" d="M21 12c0 1.66-4.03 3-9 3s-9-1.34-9-3M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/></svg> },
          { id: "debug",   label: "Отладка",   icon: <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path strokeLinecap="round" strokeLinejoin="round" d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg> },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] text-[13px] font-medium transition-all duration-150 shrink-0 whitespace-nowrap ${
              activeTab === tab.id ? "bg-white dark:bg-slate-700/70 text-slate-900 dark:text-slate-100 shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      <div key={activeTab} className="space-y-3" style={{animation:"tabContentIn 190ms cubic-bezier(0.2,0.9,0.2,1) both"}}>

      {/* ── Участники ── */}
      {activeTab === "users" && (
        <div className="space-y-3">
          <div className="relative">
            <svg className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 20 20">
              <circle cx="9" cy="9" r="5.5" /><path d="m13.5 13.5 3 3" />
            </svg>
            <input type="text" placeholder="Поиск" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-[10px] bg-slate-200/60 dark:bg-slate-700/60 pl-9 pr-8 py-2.5 text-[15px] text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-none focus:bg-slate-200/90 dark:focus:bg-slate-700/90 transition-colors"
            />
            {searchQuery && (
              <button type="button" onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-[18px] h-[18px] rounded-full bg-slate-400 dark:bg-slate-600 hover:bg-slate-500 dark:hover:bg-slate-500 transition-colors"
              >
                <svg viewBox="0 0 10 10" className="w-2.5 h-2.5 text-white" fill="none"><path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 overflow-x-auto pb-0.5 flex-1 min-w-0" style={{ scrollbarWidth: "none" }}>
              <button type="button" onClick={() => setRoleFilter(roleFilter === "online" ? "all" : "online")}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-medium transition-colors duration-150 border ${
                  roleFilter === "online"
                    ? "bg-emerald-500 text-white border-emerald-500"
                    : "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-200/70 dark:border-emerald-700/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/30"
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${roleFilter === "online" ? "bg-white/70" : (onlineCount > 0 ? "bg-emerald-400" : "bg-slate-300 dark:bg-slate-600")}`} />
                <span key={`${onlineCount}-${listKey}`} style={{ animation: "onlineNumPop 280ms cubic-bezier(0.2,0.9,0.2,1) both" }} className="tabular-nums">
                  {onlineCount === -1 ? "—" : onlineCount}
                </span>
              </button>
              {[{ key: "all", label: "Все", count: users.length }, ...Object.entries(ROLE_CONFIG).map(([key, cfg]) => ({ key, label: cfg.label, count: roleCounts[key] }))].map(({ key, label, count }) => (
                <button key={key} type="button" onClick={() => setRoleFilter(key)}
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] font-medium transition-colors duration-150 ${
                    roleFilter === key
                      ? "bg-slate-900 dark:bg-slate-600 text-white"
                      : "bg-slate-100 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                  }`}
                >
                  {label}
                  <span className={`text-[11px] tabular-nums ${roleFilter === key ? "text-white/60" : "text-slate-400 dark:text-slate-500"}`}>{count}</span>
                </button>
              ))}
            </div>
            <button type="button" onClick={doRefresh} disabled={isRefreshing}
              className="shrink-0 flex items-center justify-center w-8 h-8 rounded-full text-slate-400 dark:text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700/50 hover:text-slate-600 dark:hover:text-slate-300 transition-colors active:scale-95 disabled:pointer-events-none"
              style={{ outline: "none", WebkitTapHighlightColor: "transparent" }} title="Обновить"
            >
              <svg className="w-4 h-4" style={isRefreshing ? { animation: "spinOnce 600ms linear infinite" } : {}} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
          {usersLoading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2.5 px-3 py-2">
                <div className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-700/50 shrink-0 animate-pulse" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-2.5 w-28 rounded-full bg-slate-100 dark:bg-slate-700/50 animate-pulse" />
                  <div className="h-2 w-20 rounded-full bg-slate-100 dark:bg-slate-700/50 animate-pulse" />
                </div>
              </div>
            ))
          ) : filteredUsers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 text-center">
              <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-700/50 flex items-center justify-center mb-2">
                <svg className="w-5 h-5 text-slate-300 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <p className="text-[14px] text-slate-400 dark:text-slate-500">Никого не найдено</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-[14px] border border-slate-200/50 dark:border-slate-700/40 bg-white dark:bg-[#2a2c32] shadow-[0_1px_4px_rgba(15,23,42,0.06)] dark:shadow-none max-h-[520px] overflow-y-auto">
              {filteredUsers.map((user, i) => (
                <UserRow key={`${listKey}-${user.id}`} user={user}
                  onRoleChange={handleRoleChange} onCuratorToggle={handleCuratorToggle} onDeveloperToggle={handleDeveloperToggle}
                  onDelete={openDeleteModal} loading={roleChangeLoading}
                  currentUser={currentAdmin} isLast={i === filteredUsers.length - 1} animIndex={i}
                />
              ))}
            </div>
          )}
          {deleteConfirm.open && (
            <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-4 bg-slate-900/40 dark:bg-slate-900/60 backdrop-blur-sm">
              <div className="bg-white dark:bg-[#2a2c32] rounded-[28px] p-6 shadow-2xl w-full max-w-sm">
                <div className="w-12 h-12 rounded-2xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-red-500 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <h3 className="text-[17px] font-semibold text-slate-900 dark:text-slate-100 mb-1">Удалить участника?</h3>
                <p className="text-[14px] text-slate-500 dark:text-slate-400 mb-6 leading-relaxed">
                  <span className="font-medium text-slate-800 dark:text-slate-200">{deleteConfirm.userName}</span> будет исключён из системы. Действие необратимо.
                </p>
                <div className="flex flex-col gap-2">
                  <button onClick={confirmDelete} className="w-full h-12 rounded-2xl text-[15px] font-semibold text-white bg-red-500 hover:bg-red-600 transition-colors active:scale-[0.98]">Удалить</button>
                  <button onClick={() => setDeleteConfirm({ open: false, userId: null, userName: "" })} className="w-full h-12 rounded-2xl text-[15px] font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors">Отмена</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Выдача: приглашения + QR ── */}
      {activeTab === "vydacha" && (
        <div className="space-y-5">
          <style>{`@keyframes qrCodeIn{from{opacity:0;transform:scale(0.94) translateX(10px)}to{opacity:1;transform:scale(1) translateX(0)}}`}</style>

          {/* Приглашения */}
          <div className="space-y-3 max-w-sm">
            <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500 px-0.5">Приглашения</p>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500 mb-1.5 px-0.5">Срок действия</p>
              <div className="overflow-hidden rounded-[14px] border border-slate-200/50 dark:border-slate-700/40 bg-white dark:bg-[#2a2c32] shadow-[0_1px_4px_rgba(15,23,42,0.06)] dark:shadow-none">
                <div className="flex gap-1 p-2">
                  {[{ v: 1, label: "1 ч" }, { v: 12, label: "12 ч" }, { v: 24, label: "Сутки" }, { v: 72, label: "72 ч" }].map(({ v, label }) => (
                    <button key={v} type="button" onClick={() => { setTtlHours(v); setCustomTtl(false); }}
                      className={`flex-1 py-2 rounded-[10px] text-[13px] font-medium transition-all ${ttlHours === v && !customTtl ? "bg-slate-900 dark:bg-slate-600 text-white shadow-sm" : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50"}`}
                    >{label}</button>
                  ))}
                </div>
                <div className="h-px bg-slate-100 dark:bg-slate-700/50 mx-2" />
                <button type="button" onClick={() => setCustomTtl(true)}
                  className={`w-full px-4 py-3 text-[14px] transition-colors text-center ${customTtl ? "text-blue-600 dark:text-blue-400" : "text-slate-400 dark:text-slate-500"}`}
                >
                  {customTtl
                    ? <input type="number" autoFocus className="bg-transparent w-full text-center outline-none text-blue-600 dark:text-blue-400" placeholder="Часов..." onChange={(e) => setTtlHours(e.target.value)} />
                    : "+ Свой срок"}
                </button>
              </div>
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500 mb-1.5 px-0.5">Лимит активаций</p>
              <div className="overflow-hidden rounded-[14px] border border-slate-200/50 dark:border-slate-700/40 bg-white dark:bg-[#2a2c32] shadow-[0_1px_4px_rgba(15,23,42,0.06)] dark:shadow-none">
                <div className="flex gap-1 p-2">
                  {[{ v: 1, label: "1" }, { v: 5, label: "5" }, { v: 10, label: "10" }, { v: 0, label: "∞" }].map(({ v, label }) => (
                    <button key={v} type="button" onClick={() => { setMaxUses(v); setCustomMax(false); }}
                      className={`flex-1 py-2 rounded-[10px] text-[13px] font-medium transition-all ${maxUses === v && !customMax ? "bg-slate-900 dark:bg-slate-600 text-white shadow-sm" : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50"}`}
                    >{label}</button>
                  ))}
                </div>
                <div className="h-px bg-slate-100 dark:bg-slate-700/50 mx-2" />
                <button type="button" onClick={() => setCustomMax(true)}
                  className={`w-full px-4 py-3 text-[14px] transition-colors text-center ${customMax ? "text-blue-600 dark:text-blue-400" : "text-slate-400 dark:text-slate-500"}`}
                >
                  {customMax
                    ? <input type="number" autoFocus className="bg-transparent w-full text-center outline-none text-blue-600 dark:text-blue-400" placeholder="Лимит..." onChange={(e) => setMaxUses(e.target.value)} />
                    : "+ Свой лимит"}
                </button>
              </div>
            </div>
            <button type="button" onClick={createInvite} disabled={inviteCreateLoading}
              className="w-full h-[52px] rounded-[14px] bg-slate-900 dark:bg-slate-700 text-white text-[15px] font-semibold transition-all active:scale-[0.98] hover:bg-slate-800 dark:hover:bg-slate-600 disabled:opacity-60"
            >{inviteCreateLoading ? "Создаём..." : "Создать приглашение"}</button>
            {inviteCode && (
              <div className="overflow-hidden rounded-[14px] border border-slate-200/50 dark:border-slate-700/40 bg-white dark:bg-[#2a2c32] shadow-[0_1px_4px_rgba(15,23,42,0.06)] dark:shadow-none">
                <div className="flex items-center gap-4 px-4 py-3">
                  <div className="shrink-0 p-1.5 bg-white dark:bg-[#343640] rounded-xl border border-slate-100 dark:border-slate-700/50 shadow-sm">
                    <canvas ref={canvasRef} className="rounded-lg block" style={{ width: 72, height: 72 }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500 mb-1">Инвайт-код</p>
                    <p className="text-[22px] font-mono font-black text-slate-900 dark:text-slate-100 tracking-tighter truncate">{inviteCode}</p>
                  </div>
                </div>
                <div className="h-px bg-slate-100 dark:bg-slate-700/50" />
                <button type="button"
                  onClick={() => { navigator.clipboard.writeText(inviteLink); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                  className={`flex items-center justify-center gap-2 w-full h-11 text-[14px] font-medium transition-colors ${copied ? "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20" : "text-blue-600 dark:text-blue-400 hover:bg-slate-50 dark:hover:bg-slate-700/30"}`}
                >
                  {copied
                    ? <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>Скопировано</>
                    : <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3" /></svg>Копировать ссылку</>
                  }
                </button>
              </div>
            )}
          </div>

          <div className="h-px bg-slate-200 dark:bg-slate-700/50" />

          {/* QR-скрепки */}
          <div className="space-y-3">
            <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500 px-0.5">QR-скрепки</p>
            <div className="flex gap-5 items-start">
              <div className="space-y-3 flex-1">
                <div>
                  <p className="text-[11px] font-medium text-slate-400 dark:text-slate-500 mb-1.5">Тип награды</p>
                  <div className="flex gap-1 p-1 bg-slate-100/70 dark:bg-slate-700/40 rounded-xl">
                    {[
                      { v: "regular", label: "Обычная", icon: "/paperclip.svg" },
                      { v: "broken",  label: "Сломан.", icon: "/broken_paperclip.svg" },
                      { v: "golden",  label: "Золотая", icon: "/golden_paperclip.svg" },
                    ].map(({ v, label, icon }) => (
                      <button key={v} type="button" onClick={() => setQrRewardType(v)}
                        className={`flex-1 py-2 rounded-[9px] text-[12px] font-medium transition-all flex items-center justify-center gap-1.5 ${
                          qrRewardType === v ? "bg-white dark:bg-slate-600 text-slate-900 dark:text-slate-100 shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                        }`}
                      >
                        <img src={icon} alt="" className={`w-3.5 h-3.5 object-contain ${qrRewardType === v ? "" : "opacity-40"}`} />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: "Кол-во", value: qrAmount, onChange: (e) => setQrAmount(Math.max(1, Math.min(10, parseInt(e.target.value) || 1))), min: 1, max: 10, ph: undefined, semibold: true },
                    { label: "Часов",  value: qrTtl ?? "",      onChange: (e) => setQrTtl(e.target.value ? parseInt(e.target.value) : null),       min: 1, ph: "∞" },
                    { label: "Лимит",  value: qrMaxClaims ?? "", onChange: (e) => setQrMaxClaims(e.target.value ? parseInt(e.target.value) : null), min: 1, ph: "∞" },
                  ].map(({ label, value, onChange, min, max, ph, semibold }) => (
                    <div key={label}>
                      <p className="text-[11px] font-medium text-slate-400 dark:text-slate-500 mb-1">{label}</p>
                      <input type="number" min={min} max={max} value={value} onChange={onChange} placeholder={ph}
                        className={`w-full h-9 px-2 rounded-xl border border-slate-200 dark:border-slate-700/60 bg-white dark:bg-[#343640] text-[14px] text-slate-800 dark:text-slate-200 text-center outline-none focus:border-blue-400 dark:focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all placeholder-slate-300 dark:placeholder-slate-600 ${semibold ? "font-semibold" : ""}`}
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-[11px] font-medium text-slate-400 dark:text-slate-500 mb-1">Метка</p>
                  <input type="text" value={qrLabel} onChange={(e) => setQrLabel(e.target.value)}
                    placeholder="Для Telegram-канала, объявления..." maxLength={60}
                    className="w-full h-9 px-3 rounded-xl border border-slate-200 dark:border-slate-700/60 bg-white dark:bg-[#343640] text-[14px] text-slate-800 dark:text-slate-200 placeholder-slate-300 dark:placeholder-slate-600 outline-none focus:border-blue-400 dark:focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all"
                  />
                </div>
                <button type="button" onClick={createQrReward} disabled={qrCreateLoading}
                  className="w-full h-10 rounded-xl bg-slate-900 dark:bg-slate-700 text-white text-[14px] font-semibold transition-all active:scale-[0.98] hover:bg-slate-800 dark:hover:bg-slate-600 disabled:opacity-50"
                >{qrCreateLoading ? "Создаём..." : "Создать QR-код"}</button>
              </div>
              {qrResult && (
                <div className="flex-1 overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700/40 bg-white dark:bg-[#2a2c32] flex flex-col"
                  style={{ animation: "qrCodeIn 0.35s cubic-bezier(0.2,0.9,0.2,1) both" }}>
                  <div className="flex-1 p-4 flex items-center justify-center">
                    <canvas ref={qrCanvasRef} className="rounded-lg block w-full" style={{ aspectRatio: "1" }} />
                  </div>
                  <button type="button" onClick={downloadQr}
                    className="flex items-center justify-center gap-1.5 h-9 text-[12px] font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors border-t border-slate-100 dark:border-slate-700/50 w-full"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                    Скачать PNG
                  </button>
                </div>
              )}
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500 mb-1.5 px-0.5">История</p>
              {qrHistoryLoading ? (
                <div className="overflow-hidden rounded-[14px] border border-slate-200/50 dark:border-slate-700/40 bg-white dark:bg-[#2a2c32]">
                  {[0, 1].map((i) => (
                    <div key={i} className="flex items-center gap-3 px-4 py-3">
                      <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700/50 animate-pulse shrink-0" />
                      <div className="flex-1 space-y-1.5">
                        <div className="h-2.5 w-32 rounded-full bg-slate-100 dark:bg-slate-700/50 animate-pulse" />
                        <div className="h-2 w-20 rounded-full bg-slate-100 dark:bg-slate-700/50 animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : qrHistory.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 rounded-[14px] border border-dashed border-slate-200 dark:border-slate-700/50 bg-slate-50/50 dark:bg-transparent">
                  <p className="text-[13px] text-slate-400 dark:text-slate-500">Ещё не создавали QR-коды</p>
                </div>
              ) : (
                <div className="overflow-hidden rounded-[14px] border border-slate-200/50 dark:border-slate-700/40 bg-white dark:bg-[#2a2c32] shadow-[0_1px_4px_rgba(15,23,42,0.06)] dark:shadow-none">
                  {qrHistory.map((item, i) => {
                    const isActive = !item.is_expired && !item.is_full;
                    const typeColor = {
                      regular: "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400",
                      broken:  "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400",
                      golden:  "bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400",
                    }[item.reward_type] || "bg-slate-50 dark:bg-slate-700/30 text-slate-700 dark:text-slate-300";
                    const typeIcon = { regular: "/paperclip.svg", broken: "/broken_paperclip.svg", golden: "/golden_paperclip.svg" }[item.reward_type];
                    return (
                      <div key={item.id} className="group relative flex items-center gap-3 px-4 py-3 hover:bg-slate-50/70 dark:hover:bg-slate-700/30 transition-colors">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${typeColor}`}>
                          <img src={typeIcon} alt="" className="w-4 h-4 object-contain" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[13px] font-medium text-slate-800 dark:text-slate-200 truncate">
                            {item.label || `+${item.reward_amount} · ${{ regular: "Обычная", broken: "Сломанная", golden: "Золотая" }[item.reward_type]}`}
                          </p>
                          <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">
                            {item.claim_count}{item.max_claims ? `/${item.max_claims}` : ""} получили{" · "}
                            <span className={isActive ? "text-emerald-600 dark:text-emerald-400" : "text-slate-400 dark:text-slate-500"}>
                              {item.is_expired ? "Истёк" : item.is_full ? "Исчерпан" : "Активен"}
                            </span>
                          </p>
                        </div>
                        <button type="button" onClick={() => deleteQrReward(item.id)}
                          className="opacity-0 group-hover:opacity-100 w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-700/50 text-slate-400 dark:text-slate-500 hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-500 dark:hover:text-red-400 flex items-center justify-center transition-all shrink-0"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                        {i < qrHistory.length - 1 && <div className="pointer-events-none absolute bottom-0 left-[58px] right-0 h-px bg-slate-100 dark:bg-slate-700/50" />}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Данные: расписание + вычитка ── */}
      {activeTab === "data" && (
        <div className="space-y-5">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500 px-0.5 mb-2">Расписание</p>
            <ScheduleParseTab />
          </div>
          <div className="h-px bg-slate-200 dark:bg-slate-700/50" />
          <div className="space-y-3 max-w-sm">
            <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500 px-0.5">Вычитка</p>
            <input ref={vychitkaInputRef} type="file" accept=".xlsx" className="hidden" onChange={handleVychitkaUpload} />
            <button type="button" disabled={vychitkaUploading}
              onClick={() => { setVychitkaError(""); vychitkaInputRef.current?.click(); }}
              className="w-full h-[52px] rounded-[14px] bg-slate-900 dark:bg-slate-700 text-white text-[15px] font-semibold transition-all active:scale-[0.98] hover:bg-slate-800 dark:hover:bg-slate-600 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {vychitkaUploading ? "Загружаем..." : (
                <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>Загрузить новый файл</>
              )}
            </button>
            {vychitkaError && <p className="text-[13px] text-red-500 dark:text-red-400 px-1">{vychitkaError}</p>}
            {vychitkaSuccess && (
              <div className="flex items-start gap-2 rounded-[12px] bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700/30 px-3 py-2.5">
                <svg className="w-4 h-4 text-emerald-500 dark:text-emerald-400 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                <p className="text-[13px] text-emerald-700 dark:text-emerald-300 leading-snug">{vychitkaSuccess}</p>
              </div>
            )}
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500 mb-1.5 px-0.5">Загруженные файлы</p>
              {vychitkaLoading ? (
                <div className="overflow-hidden rounded-[14px] border border-slate-200/50 dark:border-slate-700/40 bg-white dark:bg-[#2a2c32]">
                  {Array.from({ length: 2 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3 px-4 py-3">
                      <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-700/50 animate-pulse shrink-0" />
                      <div className="flex-1 space-y-1.5">
                        <div className="h-2.5 w-40 rounded-full bg-slate-100 dark:bg-slate-700/50 animate-pulse" />
                        <div className="h-2 w-20 rounded-full bg-slate-100 dark:bg-slate-700/50 animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : vychitkaFiles.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center rounded-[14px] border border-dashed border-slate-200 dark:border-slate-700/50 bg-slate-50/50 dark:bg-transparent">
                  <svg className="w-8 h-8 text-slate-300 dark:text-slate-600 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.4"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  <p className="text-[13px] text-slate-400 dark:text-slate-500">Файлов ещё нет</p>
                </div>
              ) : (
                <div className="overflow-hidden rounded-[14px] border border-slate-200/50 dark:border-slate-700/40 bg-white dark:bg-[#2a2c32] shadow-[0_1px_4px_rgba(15,23,42,0.06)] dark:shadow-none">
                  {vychitkaFiles.map((f, i) => (
                    <div key={f.filename} className="group relative flex items-center gap-3 px-4 py-3 hover:bg-slate-50/70 dark:hover:bg-slate-700/30 transition-colors">
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center shrink-0">
                        <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.8"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-medium text-slate-800 dark:text-slate-200 truncate">{f.filename}</p>
                        {f.date_str && <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">на {f.date_str}</p>}
                      </div>
                      <button type="button" onClick={() => handleVychitkaDelete(f.filename)}
                        className="opacity-0 group-hover:opacity-100 w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-700/50 text-slate-400 dark:text-slate-500 hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-500 dark:hover:text-red-400 flex items-center justify-center transition-all shrink-0"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                      {i < vychitkaFiles.length - 1 && <div className="pointer-events-none absolute bottom-0 left-[58px] right-0 h-px bg-slate-100 dark:bg-slate-700/50" />}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Отладка ── */}
      {activeTab === "debug" && (
        <div className="space-y-4 max-w-sm">
          <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500 px-0.5">Скрепки</p>
          <div className="overflow-hidden rounded-[14px] border border-slate-200/50 dark:border-slate-700/40 bg-white dark:bg-[#2a2c32] shadow-[0_1px_4px_rgba(15,23,42,0.06)] dark:shadow-none">
            <div className="flex items-center gap-3 px-4 py-3">
              <span className={`w-2 h-2 rounded-full shrink-0 ${debugClipActive ? "bg-emerald-400" : "bg-slate-300 dark:bg-slate-600"}`} />
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-slate-800 dark:text-slate-200">
                  {debugClipActive ? "Режим отладки активен" : "Отладка неактивна"}
                </p>
                {debugClipId && <p className="text-[11px] text-slate-400 dark:text-slate-500 font-mono truncate mt-0.5">{debugClipId}</p>}
              </div>
            </div>
          </div>
          <div className="overflow-hidden rounded-[14px] border border-slate-200/50 dark:border-slate-700/40 bg-white dark:bg-[#2a2c32] shadow-[0_1px_4px_rgba(15,23,42,0.06)] dark:shadow-none">
            <button type="button" onClick={toggleDebugGolden}
              className="flex items-center justify-between w-full px-4 py-3.5 hover:bg-slate-50/70 dark:hover:bg-slate-700/30 transition-colors"
            >
              <div className="flex items-center gap-2.5">
                <img src="/golden_paperclip.svg" alt="" className="w-5 h-5 object-contain" />
                <span className="text-[14px] text-slate-800 dark:text-slate-200">Золотая скрепка</span>
              </div>
              <div className={`w-10 h-6 rounded-full transition-colors duration-200 flex items-center px-0.5 ${debugGolden ? "bg-amber-400" : "bg-slate-200 dark:bg-slate-600"}`}>
                <span className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${debugGolden ? "translate-x-4" : "translate-x-0"}`} />
              </div>
            </button>
          </div>
          <button type="button" onClick={spawnDebugClips}
            className="w-full h-[52px] rounded-[14px] bg-amber-500 hover:bg-amber-400 text-white text-[15px] font-semibold transition-all active:scale-[0.98] flex items-center justify-center gap-2"
          >
            <img src="/paperclip.svg" alt="" className="w-4 h-4 object-contain brightness-[10]" />
            Симулировать ежедневный спавн
          </button>
          {debugClipActive && (
            <button type="button" onClick={resetDebugClips}
              className="w-full h-[52px] rounded-[14px] bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 text-[15px] font-semibold transition-all active:scale-[0.98]"
            >Сбросить</button>
          )}
          <p className="text-[12px] text-slate-400 dark:text-slate-500 px-0.5 leading-relaxed">
            Скрепки появятся на текущей странице. Сбор не записывается в базу.
          </p>

          <div className="h-px bg-slate-200 dark:bg-slate-700/50" />

          <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500 px-0.5">Принудительный спавн</p>
          <button type="button" onClick={forceSpawnServer} disabled={forceSpawnLoading}
            className="w-full h-[52px] rounded-[14px] bg-slate-900 dark:bg-slate-700 hover:bg-slate-800 dark:hover:bg-slate-600 text-white text-[15px] font-semibold transition-all active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {forceSpawnLoading ? "Сбрасываем..." : "Сбросить текущее окно для всех"}
          </button>
          {forceSpawnResult && !forceSpawnResult.error && (
            <div className="rounded-[12px] bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700/30 px-3 py-2.5 space-y-0.5">
              <p className="text-[13px] font-medium text-emerald-700 dark:text-emerald-300">Сброшено {forceSpawnResult.cleared} записей</p>
              <p className="text-[11px] text-emerald-600/70 dark:text-emerald-400/60 font-mono">{forceSpawnResult.window_id}</p>
            </div>
          )}
          {forceSpawnResult?.error && (
            <p className="text-[13px] text-red-500 dark:text-red-400 px-1">Ошибка запроса</p>
          )}
          <p className="text-[12px] text-slate-400 dark:text-slate-500 px-0.5 leading-relaxed">
            Удаляет claims текущего 2-часового окна для всех. Скрепки появятся снова.
          </p>

          <div className="h-px bg-slate-200 dark:bg-slate-700/50" />

          <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500 px-0.5">Конфеты</p>
          <div className="overflow-hidden rounded-[14px] border border-slate-200/50 dark:border-slate-700/40 bg-white dark:bg-[#2a2c32] shadow-[0_1px_4px_rgba(15,23,42,0.06)] dark:shadow-none">
            <div className="flex items-center gap-3 px-4 py-3">
              <span className={`w-2 h-2 rounded-full shrink-0 ${debugCandyActive ? "bg-emerald-400" : "bg-slate-300 dark:bg-slate-600"}`} />
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-slate-800 dark:text-slate-200">
                  {debugCandyActive ? "Отладка конфет активна" : "Отладка конфет неактивна"}
                </p>
                {debugCandyId && <p className="text-[11px] text-slate-400 dark:text-slate-500 font-mono truncate mt-0.5">{debugCandyId}</p>}
              </div>
            </div>
          </div>
          <button type="button" onClick={spawnDebugCandy}
            className="w-full h-[52px] rounded-[14px] bg-orange-500 hover:bg-orange-400 text-white text-[15px] font-semibold transition-all active:scale-[0.98] flex items-center justify-center gap-2"
          >
            <img src="/candy_1.svg" alt="" className="w-4 h-4 object-contain" />
            Симулировать спавн конфет
          </button>
          {debugCandyActive && (
            <>
              <button type="button" onClick={rerollDebugCandy}
                className="w-full h-[52px] rounded-[14px] bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-[15px] font-semibold transition-all active:scale-[0.98]"
              >Перегенерировать</button>
              <button type="button" onClick={resetDebugCandy}
                className="w-full h-[52px] rounded-[14px] bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 text-[15px] font-semibold transition-all active:scale-[0.98]"
              >Сбросить</button>
            </>
          )}
          <p className="text-[12px] text-slate-400 dark:text-slate-500 px-0.5 leading-relaxed">
            Конфеты появятся на текущей странице независимо от времени. Клик не записывается в базу.
          </p>
        </div>
      )}

      </div>
    </div>
  );
}
